/**
 * Supporting Documents visibility helpers.
 *
 * Why this file exists (the problem it solves):
 * -----------------------------------------------------------------------------
 * Several provider pages show a read-only "Supporting Documents" panel (the DD
 * Review page, the Post-Assessment Discussion Review/Discussion pages, and the
 * ADHD Decision page). Each renders a link/preview for a rendered report PDF
 * (Clinical Interview, Semi-Structured Interview, QbCheck, QbTest) whenever that
 * PDF exists under `patient.documents.*Report`.
 *
 * The rendered PDF is only (re)generated when a clinician approves a report
 * section. If a section's AI paragraphs are later REGENERATED (e.g. the Lead
 * clinician takes over and edits the SSI), the section's approval is reset back
 * to the purple "AI outputs ready for review" state on the Documents Dashboard,
 * but the previously-rendered PDF is NOT automatically re-rendered. The panel
 * would therefore show a stale PDF that no longer matches the current answers.
 *
 * This helper lets those panels hide a supporting document once its section has
 * "gone back to purple", so clinicians never reference stale content.
 *
 * Why we key off `${prefix}ReportFirstApprovedAt` and not `${prefix}ReportApproved`:
 * -----------------------------------------------------------------------------
 * A section is purple whenever it is generated but not currently approved. That
 * is ALSO the normal state before a report has ever been reviewed (approval
 * happens later in the workflow, at the Documents Dashboard). Hiding on
 * "not approved" alone would wrongly hide the perfectly good first-time
 * reference PDFs during DD/PAD.
 *
 * `${prefix}ReportFirstApprovedAt` is a durable, write-once audit timestamp
 * (see reportAuditHelpers.js). It is stamped the first time a section is fully
 * approved and is NEVER cleared by any regeneration, reset, override/reverse,
 * or un-approval path. So:
 *   - FirstApprovedAt set  + currently NOT approved => it was green and has
 *     regressed to purple => the PDF may be stale => HIDE it.
 *   - FirstApprovedAt unset (never approved) => normal pre-approval reference
 *     doc => leave it visible (unchanged behaviour).
 */

/**
 * Maps a `patient.documents.*Report` key (lowercase) to its `6_manageReports`
 * report prefix (camelCase). The two conventions differ deliberately in the
 * codebase, so we centralise the mapping here.
 *
 * Only the documents that appear in the Supporting Documents panels are listed.
 */
export const SUPPORTING_DOC_REPORT_PREFIX = {
  clinicalinterviewReport: 'ci',
  semistructuredinterviewReport: 'ssi',
  qbcheckReport: 'qbCheck',
  qbtestReport: 'qbTest',
};

/**
 * Returns true when the supporting document for `docKey` should be treated as
 * potentially stale because its report section has regressed from approved
 * (green) back to "AI outputs ready for review" (purple).
 *
 * @param {Object|undefined} manageReports - patient.dataModel['6_manageReports']
 * @param {string} docKey - a `documents.*Report` key (e.g. 'semistructuredinterviewReport')
 * @returns {boolean} true => hide the document; false => safe to show
 */
export const isSupportingDocStale = (manageReports, docKey) => {
  const prefix = SUPPORTING_DOC_REPORT_PREFIX[docKey];
  // Unknown/unmapped doc types are never gated (fail-open: keep showing them).
  if (!prefix) return false;

  const everApproved = Boolean(manageReports?.[`${prefix}ReportFirstApprovedAt`]);
  const currentlyApproved = manageReports?.[`${prefix}ReportApproved`] === true;

  return everApproved && !currentlyApproved;
};
